import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Check, ArrowRight } from "lucide-react";

/**
 * Fecha de vencimiento de la propuesta: 7 días hábiles desde la presentación.
 * Editable — ajustar según la fecha real de presentación.
 */
const DEADLINE = new Date("2026-08-15T21:59:59Z");

const includes = [
  "Plataforma Estratégica de Marca Personal (Riel A completo)",
  "Acompañamiento CEO Visible 90 días (Riel B completo)",
  "Piloto de Voz con criterio estratégico",
  "Los diez entregables",
  "Definición del equipo 360, OKRs y KPIs",
];

const useCountdown = (deadline: Date) => {
  const calc = () => {
    const diff = deadline.getTime() - Date.now();
    if (diff <= 0) return { d: 0, h: 0, m: 0, s: 0 };
    return {
      d: Math.floor(diff / (1000 * 60 * 60 * 24)),
      h: Math.floor((diff / (1000 * 60 * 60)) % 24),
      m: Math.floor((diff / (1000 * 60)) % 60),
      s: Math.floor((diff / 1000) % 60),
    };
  };
  const [t, setT] = useState(calc);
  useEffect(() => {
    const id = setInterval(() => setT(calc()), 1000);
    return () => clearInterval(id);
  }, []);
  return t;
};

const TimeBox = ({ value, label }: { value: number; label: string }) => (
  <div
    className="rounded-xl px-4 py-3 md:px-5 md:py-4 text-center border min-w-[68px] md:min-w-[88px]"
    style={{
      background: "rgba(91,163,224,0.08)",
      borderColor: "rgba(91,163,224,0.25)",
    }}
  >
    <div className="font-display text-2xl md:text-4xl font-bold text-white tabular-nums">
      {String(value).padStart(2, "0")}
    </div>
    <div className="text-[10px] md:text-xs uppercase tracking-[0.2em] text-white/60 mt-1">
      {label}
    </div>
  </div>
);

const ChiappellaInversion = () => {
  const { d, h, m, s } = useCountdown(DEADLINE);

  const scrollToFinal = () => {
    const el = document.getElementById("cta-final");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="inversion"
      className="py-24 md:py-32 relative overflow-hidden"
      style={{
        background:
          "linear-gradient(180deg, hsl(220 69% 2%), hsl(217 61% 8%))",
      }}
    >
      <div className="container px-4 max-w-5xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-center mb-12"
        >
          <span
            className="text-sm font-semibold tracking-[0.25em] uppercase mb-4 block"
            style={{ color: "#5BA3E0" }}
          >
            Inversión
          </span>
          <h2 className="font-display text-3xl md:text-5xl lg:text-6xl font-bold text-white leading-[1.1] mb-5">
            Fase 1
            <br />
            <span style={{ color: "#5BA3E0" }}>Fundación de Autoridad.</span>
          </h2>
          <p className="text-base md:text-lg text-white/70">
            Noventa días · dos rieles simultáneos · diez entregables
          </p>
        </motion.div>

        {/* Countdown */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="text-center mb-12"
        >
          <p className="text-xs md:text-sm uppercase tracking-[0.25em] text-white/60 mb-4">
            Esta propuesta vence en
          </p>
          <div className="flex items-center justify-center gap-2 md:gap-3 flex-wrap">
            <TimeBox value={d} label="Días" />
            <TimeBox value={h} label="Horas" />
            <TimeBox value={m} label="Min" />
            <TimeBox value={s} label="Seg" />
          </div>
        </motion.div>

        {/* Main card */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.25 }}
          className="rounded-3xl border border-white/10 p-7 md:p-12"
          style={{
            background:
              "linear-gradient(135deg, hsl(217 61% 10%), hsl(215 48% 16%))",
          }}
        >
          {/* Price */}
          <div className="text-center mb-8">
            <p className="text-sm uppercase tracking-[0.2em] text-white/60 mb-3">
              Precio de lista
            </p>
            <div className="flex items-baseline justify-center gap-3">
              <span className="font-display text-5xl md:text-7xl lg:text-8xl font-bold text-white">
                USD 21.000
              </span>
            </div>
            <p className="text-sm text-white/55 mt-2">+ IVA</p>
          </div>

          {/* Single payment badge */}
          <div className="flex justify-center mb-4">
            <div
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm md:text-base text-white"
              style={{
                background: "linear-gradient(135deg, #0F6E56, #1D9E75)",
              }}
            >
              Pago único al inicio: USD 18.000 + IVA
            </div>
          </div>

          <p className="text-center text-sm md:text-base text-white/70 mb-10">
            Noventa días. Dos rieles simultáneos. Diez entregables. El carro
            andando.
          </p>

          {/* Includes */}
          <div className="mb-10">
            <h3 className="font-display text-xl md:text-2xl font-semibold text-white mb-5">
              Todo incluido
            </h3>
            <ul className="grid md:grid-cols-2 gap-3">
              {includes.map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <span
                    className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center"
                    style={{ background: "#1D9E75" }}
                  >
                    <Check className="w-3 h-3 text-white" strokeWidth={3} />
                  </span>
                  <span className="text-sm md:text-base text-white/85 leading-relaxed">
                    {item}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* Notice */}
          <div
            className="rounded-xl p-5 md:p-6 mb-10 border-l-4"
            style={{
              borderColor: "#5BA3E0",
              background: "rgba(91,163,224,0.06)",
            }}
          >
            <p className="font-semibold text-white mb-2">
              Propuesta exclusiva para Gastón Nicolás Parada
            </p>
            <p className="text-sm md:text-base text-white/75 leading-relaxed">
              Validez de 7 días hábiles desde su presentación. Cualquier
              alternativa de financiación se conversa en la sesión de
              presentación.
            </p>
          </div>

          {/* CTA */}
          <button
            onClick={scrollToFinal}
            className="group w-full inline-flex items-center justify-center gap-3 px-8 py-5 rounded-full text-base md:text-lg font-semibold text-white transition-all duration-300 hover:scale-[1.01]"
            style={{
              background: "linear-gradient(135deg, #1d4a8c 0%, #3B8BD4 100%)",
              boxShadow: "0 14px 40px -12px rgba(59,139,212,0.55)",
            }}
          >
            Coordinar la sesión
            <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default ChiappellaInversion;
